import React, { useEffect, useState } from 'react';
import { RefreshCcw } from 'react-feather';
import { Dropdown, Input, Select, Button, Pagination, Badge, Descriptions } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { useToast } from '../../Toaster/ToastContext';
import { apiClient } from '../../services/apiClient';
import SalesModal from '../../core/modals/sales/SalesModal';
import { PlusCircle } from 'feather-icons-react/build/IconComponents';
import Loader from '../loader/loader';
import PropTypes from 'prop-types';

const CustomSaleDetailModal = ({ open, onClose, sale }) => {
    if (!open) return null;

    return (
        <div className="custom-modal-overlay">
            <div className="custom-modal">
                <div className="modal-header">
                    <h2>Sale Details</h2>
                    <button className="close-btn" onClick={onClose}>
                        ×
                    </button>
                </div>
                <div className="modal-body">
                    {sale ? (
                        <Descriptions
                            bordered
                            column={1}
                            size="middle"
                            className="sale-descriptions"
                            labelStyle={{ fontWeight: 'bold', width: '30%' }}
                            contentStyle={{ width: '70%' }}
                        >
                            <Descriptions.Item label="Customer">{sale.customer || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Reference">{sale.reference || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Date">{sale.date || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Badge
                                    status={sale.status === 'Completed' ? 'success' : 'warning'}
                                    text={sale.status || 'N/A'}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="Grand Total">${sale.grand_total || '0.00'}</Descriptions.Item>
                            <Descriptions.Item label="Paid">${sale.paid || '0.00'}</Descriptions.Item>
                            <Descriptions.Item label="Due">${sale.due || '0.00'}</Descriptions.Item>
                            <Descriptions.Item label="Payment Status">
                                <span
                                    className={`badge badge-soft-${
                                        sale.payment_status === 'Paid'
                                            ? 'success'
                                            : sale.payment_status === 'Unpaid'
                                            ? 'warning'
                                            : 'danger'
                                    } shadow-none badge-xs`}
                                >
                                    <i className="ti ti-point-filled me-1"></i>
                                    {sale.payment_status || 'N/A'}
                                </span>
                            </Descriptions.Item>
                        </Descriptions>
                    ) : (
                        <p>No sale details available.</p>
                    )}
                </div>
                <div className="modal-footer">
                    <Button key="close" onClick={onClose} type="primary">
                        Close
                    </Button>
                </div>
            </div>
        </div>
    );
};

CustomSaleDetailModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    sale: PropTypes.shape({
        customer: PropTypes.string,
        reference: PropTypes.string,
        date: PropTypes.string,
        status: PropTypes.string,
        grand_total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        paid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        due: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        payment_status: PropTypes.string,
    }),
};

const SalesList = () => {
    const { showToast } = useToast();
    const [open, setOpen] = useState(false);
    const [sales, setSales] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [searchCustomer, setSearchCustomer] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedSale, setSelectedSale] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10,
    });

    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [selectedSort, setSelectedSort] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const handleStatusSelect = ({ key }) => {
        setSelectedStatus(key === selectedStatus ? null : key);
        fetchSales(10, selectedCustomer, key === selectedStatus ? null : key, selectedPayment, selectedSort);
    };

    const handlePaymentSelect = ({ key }) => {
        setSelectedPayment(key === selectedPayment ? null : key);
        fetchSales(10, selectedCustomer, selectedStatus, key === selectedPayment ? null : key, selectedSort);
    };

    const handleSortSelect = ({ key }) => {
        let label = '';
        switch (key) {
            // case 'recent': label = 'Recently Added'; break;
            case 'asc': label = 'Ascending'; break;
            case 'desc': label = 'Descending'; break;
            // case 'lastMonth': label = 'Last Month'; break;
            // case 'last7': label = 'Last 7 Days'; break;
            default: label = key;
        }
        setSelectedSort(label);
        fetchSales(10, selectedCustomer, selectedStatus, selectedPayment, key);
    };

    const fetchSales = async (per_page = 10, customer = null, status = null, paymentStatus = null, sort = '') => {
        setLoading(true);
        try {
            const response = await apiClient.post('/get-all-sale', {
                per_page, 
                customer: customer,
                status,
                payment_status: paymentStatus,
                sort
            });
            if (response.status) {
                setSales(response.sales || []);
                setPagination(response.pagination);
            } else {
                showToast('Sales could not be fetched. Please try again.', 'error', 'Sales');
            }
        } catch (err) {
            showToast('Failed to fetch sales. Try again.', 'error', 'Sales');
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomers = async () => {
        try {
            const res = await apiClient.get('/get-all-customer');
            if (res.success) setCustomers(res.data || []);
        } catch (err) {
            showToast('Failed to fetch customers.', 'error', 'Customers');
        }
    };

    useEffect(() => {
        fetchCustomers();
        fetchSales();
    }, []);

    const handleCustomerFilter = (id) => {
        setSelectedCustomer(id);
        fetchSales(10, id);
    };

    const handlePageChange = (page) => {
        fetchSales(page, selectedCustomer);
    };

    const handleAddSale = async (salePayload) => {
        try {
            const response = await apiClient.post('/store-sale', salePayload);
            if (response?.status === false) {
                showToast(response.message || "Failed to place order.", "error", "POS");
                throw new Error(response.message || "Failed to place order.");
            }
            showToast(response.message || "Order placed successfully!", "success", "POS");
            setOpen(false);
            fetchSales(pagination.per_page, selectedCustomer);
            return response;
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || "Failed to place order. Try again.";
            showToast(message, "error", "POS");

            throw err;
        }
    };



    const filteredCustomers = customers.filter((cust) =>
        cust.customer_name.toLowerCase().includes(searchCustomer.toLowerCase())
    );

    const handleActionClick = ({ key }, sale) => {
        console.log(sale);
        switch (key) {
            case 'view':
                // handleView(saleId);
                setSelectedSale(sale);
                setDetailModalOpen(true);
                break;
            case 'edit':
                // handleEdit(saleId);
                break;
            case 'show':
                // handleShowPayments(saleId);
                break;
            case 'create':
                // handleCreatePayment(saleId);
                break;
            case 'pdf':
                // handleDownloadPDF(saleId);
                break;
            case 'return':
                handleSalesReturn(sale);
                break;
            default:
                break;
        }
    };

    const handleSalesReturn = async (sale) => {
        try {
            setLoading(true);
            const response = await apiClient.post('/return-sale', {
                sale_id: sale.id
            });
            if (response.status) {
                showToast(response.message || "Sales return successfully!", "success", "Sales Return");
                fetchSales(pagination.per_page, selectedCustomer);
            } else {
                showToast(response.message || "Failed to return sale. Try again.", "error", "Sales Return");
            }
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || "An error occurred. Try again.";
            showToast(message, "error", "Sales Return");
        } finally {
            setLoading(false);
        }
    };

    const getSaleActionsMenu = (sale) => ({
        items: [
            { key:'', label: ''},
            { key: 'view', label: 'Sale Detail' },
            // { key: 'edit', label: 'Edit Sale' },
            // { key: 'show', label: 'Show Payments' },
            // { key: 'create', label: 'Create Payment' },
            // { key: 'pdf', label: 'Download PDF' },
            { key: 'return', label: 'Sales Return' }
        ],
        onClick: (info) => handleActionClick(info, sale)
    });

    const filteredSales = sales.filter(sale => 
        (sale.customer?.toLowerCase().includes(searchTerm.toLowerCase()) || '') &&
        (sale.status?.toLowerCase().includes(searchTerm.toLowerCase()) || '') &&
        (sale.reference?.toLowerCase().includes(searchTerm.toLowerCase()) || '') &&
        (sale.payment_status?.toLowerCase().includes(searchTerm.toLowerCase()) || '')
    );

    return (
        <>
            <Loader loading={loading} />
            <div className="page-wrapper">
                <div className="content">
                    <div className="page-header">
                        <div className="add-item d-flex">
                            <div className="page-title">
                                <h4>Sales</h4>
                                <h6>Manage Your Sales</h6>
                            </div>
                        </div>
                        <ul className="table-top-head">
                            {/* <li>
                                <a title="Pdf">
                                    <img src="assets/img/icons/pdf.svg" alt="pdf" />
                                </a>
                            </li>
                            <li>
                                <a title="Excel">
                                    <img src="assets/img/icons/excel.svg" alt="excel" />
                                </a>
                            </li> */}
                            <li>
                                <a title="Refresh" onClick={() => fetchSales(pagination.per_page, selectedCustomer)}>
                                    <RefreshCcw />
                                </a>
                            </li>
                        </ul>
                        <div className="page-btn">
                            <Button className="btn btn-primary d-flex gap-2 align-items-center" onClick={() => setOpen(true)}>
                                <PlusCircle size={14} /> Add Sales
                            </Button>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                            <div className="search-set">
                                <div className="search-input">
                                    <Input
                                        placeholder="Search"
                                        prefix={<i className="ti ti-search fs-14 feather-search"></i>}
                                        className="input"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Customer"
                                    className="me-2"
                                    style={{ width: 200 }}
                                    onSearch={setSearchCustomer}
                                    onChange={handleCustomerFilter}
                                    filterOption={false}
                                >
                                    {filteredCustomers.map((cust) => (
                                        <Select.Option key={cust.customer_name} value={cust.customer_name}>
                                            {cust.customer_name}
                                        </Select.Option>
                                    ))}
                                </Select>

                                <Dropdown
                                    menu={{
                                        onClick: handleStatusSelect,
                                        items: [
                                            { key: '', label: ''},
                                            { key: 'completed', label: 'Completed' },
                                            { key: 'returned', label: 'Returned' }
                                        ]
                                    }}
                                >
                                    <Button className="me-2">
                                        {selectedStatus ? `Status: ${selectedStatus}` : 'Status'}
                                    </Button>
                                </Dropdown>

                                <Dropdown
                                    menu={{
                                        onClick: handlePaymentSelect,
                                        items: [
                                            { key: '', label: ''},
                                            { key: 'paid', label: 'Paid' },
                                            { key: 'unpaid', label: 'Unpaid' },
                                        ]
                                    }}
                                >
                                    <Button className="me-2">
                                        {selectedPayment ? `Payment: ${selectedPayment}` : 'Payment Status'}
                                    </Button>
                                </Dropdown>

                                <Dropdown
                                    menu={{
                                        onClick: handleSortSelect,
                                        items: [
                                            { key: '', label: ''},
                                            // { key: 'recent', label: 'Recently Added' },
                                            { key: 'asc', label: 'Ascending' },
                                            { key: 'desc', label: 'Descending' },
                                            // { key: 'lastMonth', label: 'Last Month' },
                                            // { key: 'last7', label: 'Last 7 Days' }
                                        ]
                                    }}
                                >
                                    <Button>
                                        Sort By: {selectedSort}
                                    </Button>
                                </Dropdown>

                            </div>
                        </div>

                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table datatable">
                                    <thead className="thead-light">
                                        <tr>
                                            {/* <th>
                                                <label className="checkboxs">
                                                    <input type="checkbox" />
                                                    <span className="checkmarks"></span>
                                                </label>
                                            </th> */}
                                            <th>Customer</th>
                                            <th>Reference</th>
                                            <th>Date</th>
                                            <th>Status</th>
                                            <th>Grand Total</th>
                                            <th>Paid</th>
                                            <th>Due</th>
                                            <th>Payment Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="sales-list">
                                        {filteredSales.map((sale) => (
                                            <tr key={sale.id}>
                                                {/* <td>
                                                    <label className="checkboxs">
                                                        <input type="checkbox" />
                                                        <span className="checkmarks"></span>
                                                    </label>
                                                </td> */}
                                                <td><a href="#">{sale.customer}</a></td>
                                                <td>{sale.reference}</td>
                                                {/* <td>{sale.date}</td> */}
                                                <td>
                                                    {sale.date 
                                                    ? new Date(sale.date).toLocaleDateString('en-GB', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric'
                                                    }).replace(/\//g, '-') 
                                                    : 'N/A'}
                                                </td>
                                                <td>
                                                    <Badge status={sale.status === 'Completed' ? 'success' : 'warning'} text={sale.status} />
                                                </td>
                                                <td>${sale.grand_total}</td>
                                                <td>${sale.paid}</td>
                                                <td>${sale.due}</td>
                                                <td>
                                                    <span className={`badge badge-soft-${sale.payment_status === 'Paid' ? 'success' : sale.payment_status === 'Unpaid' ? 'warning' : 'danger'} shadow-none badge-xs`}>
                                                        <i className="ti ti-point-filled me-1"></i>{sale.payment_status}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <Dropdown
                                                        menu={getSaleActionsMenu(sale)}
                                                        trigger={['click']}
                                                    >
                                                        <Button icon={<MoreOutlined />} />
                                                    </Dropdown>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex justify-content-end p-3">
                                <Pagination
                                    current={pagination.current_page}
                                    total={pagination.total}
                                    pageSize={pagination.per_page}
                                    onChange={handlePageChange}
                                />
                            </div>
                        </div>
                    </div>
                    <SalesModal open={open} onClose={() => setOpen(false)} onSubmit={handleAddSale} />
                    <CustomSaleDetailModal open={detailModalOpen} onClose={() => setDetailModalOpen(false)} sale={selectedSale} />
                </div>
            </div>
        </>
    );
};

export default SalesList;
